/**
 * The display name a guest plays under, remembered on this device so the
 * Together tab does not ask again and a join link can join at once.
 *
 * A guest never has to type one (owner decision, 2026-10-04): the first time
 * a name is asked for, the device is given one, "Guest" and a four-digit
 * number, and keeps it. It is still a field the guest may change, and what
 * they type is what is remembered from then on.
 */
import { safeLocalStorage } from './settings-io.js';

export const GUEST_NAME_KEY = 'interlock:guest-name';

/** A ready-made guest name: "Guest" and a number from 1000 to 9999. */
export function generateGuestName(random: () => number = Math.random): string {
  const number = 1000 + Math.min(8999, Math.floor(random() * 9000));
  return `Guest ${String(number)}`;
}

/**
 * The name this device plays under as a guest: the one remembered, or, when
 * there is none yet (or only blanks), a new ready-made one, remembered at
 * once so it is the same next time. Without storage it is a fresh name each
 * visit, which still never blocks a start.
 */
export function loadGuestName(
  storage: Storage | undefined = safeLocalStorage(),
  random: () => number = Math.random,
): string {
  const stored = readStored(storage);
  if (stored !== '') return stored;
  const made = generateGuestName(random);
  saveGuestName(made, storage);
  return made;
}

function readStored(storage: Storage | undefined): string {
  try {
    return storage?.getItem(GUEST_NAME_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

export function saveGuestName(
  name: string,
  storage: Storage | undefined = safeLocalStorage(),
): void {
  if (storage === undefined) return;
  try {
    storage.setItem(GUEST_NAME_KEY, name.trim());
  } catch {
    // Private browsing, or storage full. Not worth interrupting for.
  }
}
